import { useCallback, useContext, useId, useMemo, useState } from 'react'
import { useIntl, type IntlShape } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import LabeledSheet from 'LuxKit/LabeledSheet'
import Label from 'LuxKit/Label/Label'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineAngleUpIcon from 'LuxKit/Icons/line/LineAngleUpIcon'
import PriceDetails from 'tripPlanner/components/ItemOfferPrice/PriceDetails'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import LuxPlusInclusions from 'luxPlus/components/LuxPlusInclusions'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import determineOfferFeatureSymbol from 'lib/offer/determineOfferFeatureSymbol'
import { formatInclusionsTitleBonus } from 'constants/inclusions'
import { useAppSelector } from 'hooks/reduxHooks'
import { getInclusionsViewableLuxPlusTier } from 'selectors/luxPlusSelectors'
import useListTruncator from 'hooks/useListTruncator'
import AddHotelToTripButton from 'tripPlanner/components/AddToTrip/AddHotelToTripButton'
import type { SendMessageOptions } from 'tripPlanner/components/AddToTrip/useAddHotelToTrip'
import type {
  HotelRoomOptionInclusion,
  HotelRoomOptionsRate,
  HotelRoomOptionsResult,
  TripItineraryData,
} from 'tripPlanner/api/types'
import type { HotelRoomOption } from 'tripPlanner/types/hotelRoomOption'
import CurrencyContext from 'contexts/currencyContext'
import ChatHotelRoomOptionCancellationPolicy from './ChatHotelRoomOptionCancellationPolicy'
import {
  DEFAULT_VISIBLE_INCLUSION_COUNT,
  RATE_PRICE_COLUMN_WIDTH,
  ROOM_CARD_HORIZONTAL_BREAKPOINT,
} from './chatHotelRoomOptionsLayout'

export const RateRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${rem(16)};
  padding: ${rem(16)};

  @container (min-width: ${rem(ROOM_CARD_HORIZONTAL_BREAKPOINT)}) {
    grid-template-columns: minmax(0, 1fr) ${rem(RATE_PRICE_COLUMN_WIDTH)};
    gap: ${rem(24)};
    padding: ${rem(20)} ${rem(24)};
  }
`

const RateDetails = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
`

const RateHeading = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(2)};
`

const InclusionGroups = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
`

const InclusionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
`

const BonusInclusionList = styled(InclusionList)`
  padding: ${rem(16)} ${rem(16)} ${rem(12)};
`

export const PriceActionsColumn = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${rem(20)};
`

function getPriceLabel(
  nights: number | undefined,
  pricingMode: HotelRoomOptionsResult['pricingMode'],
  intl: IntlShape,
): string {
  if (nights != null) {
    return intl.formatMessage({
      id: 'pDdaHwY_41NbMkskomHR',
      defaultMessage: '{count, plural, one {# night} other {# nights}} from',
      description: 'Hotel room option pricing caption showing number of nights in trip planner chat',
    }, { count: nights })
  }

  if (pricingMode === 'exact_dates') {
    return intl.formatMessage({
      id: 'igpA1yeOHDInsmyjU2pu',
      defaultMessage: 'Live total from',
      description: 'Exact-date live price caption in trip planner chat hotel room options',
    })
  }

  return intl.formatMessage({
    id: 'bkopEEiSPXkUwWIWajXd',
    defaultMessage: 'Current from price',
    description: 'Duration-only current price caption in trip planner chat hotel room options',
  })
}

function getBonusInclusionsLabel(nights: number | undefined, intl: IntlShape): string {
  const title = formatInclusionsTitleBonus(intl)

  // The payload carries no minimum-stay metadata, so only the stay we actually
  // priced can qualify the label. Without a duration it stays unqualified.
  if (nights == null) return title

  return intl.formatMessage(
    {
      id: 'QjW4q3TCsRyYp1zjXeLb',
      defaultMessage: '{title} for {count, plural, one {a # night stay} other {# night stays}}',
      description: 'Label on the extra inclusions box for a hotel room package in trip planner chat',
    },
    { title, count: nights },
  )
}

function mapRoomOptionInclusion(
  inclusion: HotelRoomOptionInclusion,
  index: number,
): App.PackageInclusion {
  return {
    id: `${inclusion.text}-${index}`,
    description: inclusion.text,
    symbol: determineOfferFeatureSymbol(inclusion.icon ?? inclusion.text),
    isHighlighted: inclusion.isHighlighted,
    isBonus: inclusion.isBonus,
  }
}

function RatePriceContent({
  rate,
  price,
  currencyCode,
  pricingMode,
  nights,
}: {
  rate: HotelRoomOptionsRate
  price?: number
  currencyCode: string
  pricingMode: HotelRoomOptionsResult['pricingMode']
  nights?: number
}) {
  const intl = useIntl()

  if (price == null) {
    return (
      <BodyText variant="medium" weight="bold">
        {intl.formatMessage({
          id: 'AuqQy3eGXWgQkUu4hmmd',
          defaultMessage: 'Price unavailable',
          description: 'Unavailable price label in trip planner chat hotel room options',
        })}
      </BodyText>
    )
  }

  const value = rate.valuedUpToPrice ?? undefined
  const discount = value && price && value > price ? getValueOffPercent(value, price) : undefined

  return (
    <PriceDetails
      price={price}
      luxPlusPrice={rate.lowestLuxPlusMemberPrice}
      currencyCode={currencyCode}
      priceLabel={getPriceLabel(nights, pricingMode, intl)}
      totalLabel="total"
      value={value}
      discount={discount}
      discountDisplayType="following"
      showTaxesAndFees={rate.amountIncludes === 'all_in'}
    />
  )
}

interface Props {
  rate: HotelRoomOptionsRate
  room: HotelRoomOptionsResult['roomOptions'][number]
  result: HotelRoomOptionsResult
  itineraryData?: TripItineraryData
  onSendMessage?: (message: string, options?: SendMessageOptions) => void
  currencyCode?: string
  pricingMode: HotelRoomOptionsResult['pricingMode']
  nights?: number
  checkIn?: string
  isRoomOptionSelected?: boolean
}

function ChatHotelRoomOptionsRateOption({
  rate,
  room,
  result,
  itineraryData,
  onSendMessage,
  currencyCode,
  pricingMode,
  nights,
  checkIn,
  isRoomOptionSelected = false,
}: Props) {
  const intl = useIntl()
  const currentCurrency = useContext(CurrencyContext)
  const viewableLuxPlusTier = useAppSelector(getInclusionsViewableLuxPlusTier)
  const inclusionsId = useId()
  const [inclusionsExpanded, setInclusionsExpanded] = useState(false)
  const toggleInclusions = useCallback(() => setInclusionsExpanded((expanded) => !expanded), [])

  const title = rate.packageName ?? rate.rateName ?? intl.formatMessage({
    id: 'KeNDhBSRgXPfvaRmwiOr',
    defaultMessage: 'Package option',
    description: 'Fallback package title in trip planner chat hotel room options',
  })
  // Both pre-add and post-add states read "Select" here: this widget exists to pick
  // a package, and the add-to-trip modal the first click opens says so itself.
  const selectCtaLabels = useMemo(() => {
    const select = intl.formatMessage({
      id: 'uUkfls4UYiBDUOvL6vx2',
      defaultMessage: 'Select',
      description: 'Button label selecting a hotel room package in the trip planner chat room options widget',
    })
    return {
      add: select,
      select,
      selected: intl.formatMessage({
        id: 'xcazbpKNVpZ3nTH58Nkn',
        defaultMessage: 'Selected',
        description: 'Disabled button label for the hotel room package already selected in the trip planner chat room options widget',
      }),
    }
  }, [intl])

  const bonusInclusions = useMemo(
    () => rate.inclusions?.filter((inclusion) => inclusion.isBonus).map(mapRoomOptionInclusion) ?? [],
    [rate.inclusions],
  )
  const standardInclusions = useMemo(
    () => rate.inclusions?.filter((inclusion) => !inclusion.isBonus).map(mapRoomOptionInclusion) ?? [],
    [rate.inclusions],
  )
  const luxPlusInclusions = useMemo(
    () => rate.luxPlusInclusions
      ?.filter((inclusion) => inclusion.luxPlusTier === viewableLuxPlusTier)
      .map(mapRoomOptionInclusion) ?? [],
    [rate.luxPlusInclusions, viewableLuxPlusTier],
  )
  const [alwaysVisibleInclusions, collapsibleInclusions] = useListTruncator(
    DEFAULT_VISIBLE_INCLUSION_COUNT,
    standardInclusions,
  )
  const visibleInclusions = inclusionsExpanded ? standardInclusions : alwaysVisibleInclusions

  const roomOption = useMemo<HotelRoomOption>(() => ({
    packageId: rate.packageId,
    packageName: rate.packageName,
    durationInDays: result.nights,
    roomTypeId: room.roomTypeId,
    roomTypeName: room.roomName,
    rateId: rate.roomRateId,
    checkIn: result.checkIn,
    checkOut: result.checkOut,
    occupancies: result.rooms,
  }), [rate.packageId, rate.packageName, rate.roomRateId, result.checkIn, result.checkOut, result.nights, result.rooms, room.roomName, room.roomTypeId])

  return (
    <RateRow>
      <RateDetails data-testid="chat-rate-details">
        <RateHeading>
          <BodyText variant="medium" weight="bold">{title}</BodyText>
          {rate.rateName && rate.rateName !== title && (
            <BodyText variant="small" colour="neutral-three">{rate.rateName}</BodyText>
          )}
          <ChatHotelRoomOptionCancellationPolicy
            cancellationPolicy={rate.cancellationPolicy}
            checkIn={checkIn}
          />
        </RateHeading>

        {(luxPlusInclusions.length > 0 || bonusInclusions.length > 0 || standardInclusions.length > 0) && (
          <InclusionGroups>
            {luxPlusInclusions.length > 0 && (
              <LuxPlusInclusions
                luxPlusInclusions={luxPlusInclusions}
                hideUpsellModal
              />
            )}
            {bonusInclusions.length > 0 && (
              <LabeledSheet
                /* `highlight` is the kind every other extra-inclusions box in the product
                   uses (see BonusInclusionsLabeledContainer). The design's green comes from
                   a Figma variable the theme has not been repointed to yet, and reaching for
                   `highlight-secondary` to chase it renders purple. */
                kind="highlight"
                /* Passed as an element purely so the label can be sentence-cased; the
                   kind/variant pair matches what LabeledSheet builds for a string label. */
                label={(
                  <Label kind="primary" variant="highlight-primary-dark" format="propercase">
                    {getBonusInclusionsLabel(nights, intl)}
                  </Label>
                )}
                data-testid="chat-room-bonus-inclusions"
              >
                <BonusInclusionList>
                  {bonusInclusions.map((inclusion) => (
                    <OfferInclusionItem
                      key={inclusion.id}
                      inclusion={inclusion}
                      isHighlighted
                      size="medium"
                    />
                  ))}
                </BonusInclusionList>
              </LabeledSheet>
            )}
            {visibleInclusions.length > 0 && (
              <InclusionList id={inclusionsId}>
                {visibleInclusions.map((inclusion) => (
                  <OfferInclusionItem
                    key={inclusion.id}
                    inclusion={inclusion}
                    size="medium"
                  />
                ))}
              </InclusionList>
            )}
            {collapsibleInclusions.length > 0 && (
              <div>
                <TextLink
                  size="medium"
                  weight="bold"
                  underline
                  aria-expanded={inclusionsExpanded}
                  aria-controls={inclusionsId}
                  onClick={toggleInclusions}
                  endIcon={inclusionsExpanded ? <LineAngleUpIcon size="S" /> : <LineAngleDownIcon size="S" />}
                >
                  {inclusionsExpanded
                    ? intl.formatMessage({
                        id: 'WN8sGK6Ne4PmiXTwL5cH',
                        defaultMessage: 'View fewer inclusions',
                        description: 'Link collapsing the truncated inclusions on a hotel room package in trip planner chat',
                      })
                    : intl.formatMessage({
                        id: '1UFdLr3tGd44Y-JD5M3j',
                        defaultMessage: 'View more inclusions',
                        description: 'Link expanding the truncated inclusions on a hotel room package in trip planner chat',
                      })}
                </TextLink>
              </div>
            )}
          </InclusionGroups>
        )}
      </RateDetails>

      <PriceActionsColumn data-testid="chat-rate-price-actions">
        <RatePriceContent
          rate={rate}
          price={rate.lowestNonMemberPrice ?? undefined}
          currencyCode={currencyCode ?? result.currencyCode ?? currentCurrency}
          pricingMode={pricingMode}
          nights={nights}
        />
        {onSendMessage && (
          <AddHotelToTripButton
            itineraryData={itineraryData}
            hotelName={result.hotelName}
            offerId={result.offerId}
            onSendMessage={onSendMessage}
            filterNights={result.nights}
            kind="primary"
            size="medium"
            fit="full-width"
            hideActionIcons
            roomOption={roomOption}
            isRoomOptionSelected={isRoomOptionSelected}
            ctaLabels={selectCtaLabels}
          />
        )}
      </PriceActionsColumn>
    </RateRow>
  )
}

export default ChatHotelRoomOptionsRateOption
